/**
 * Pre-curated diligence question packages, scoped per persona.
 *
 * Audience: an exec (Walker Blue's, or a diligence team) who landed
 * in the Hub for the first time and doesn't know what to ask. Each
 * package is a 4–5 question track that surfaces
 * the standard executive lens for THAT persona's domain — financial
 * for the CFO, operational for the COO, and so on.
 *
 * Click on a package card in the EmptyState → fires the first
 * question. The rest of the package's questions show up as
 * suggested follow-ups so the user can drill in at their own pace.
 */

export interface DiligencePackage {
  id: string;
  title: string;
  description: string;
  /** Hex tone for the card accent — picks an established palette family. */
  accent: 'wb' | 'emerald' | 'amber' | 'rose' | 'violet' | 'teal' | 'sky';
  questions: string[];
}

/**
 * Single-question prompt for the "Or ask a single question" row at the
 * bottom of the EmptyState. Stored per-persona because each executive
 * has a different mix of "common one-shot questions you'd start with".
 */
export interface SingleSuggestion {
  category: string;
  text: string;
}

const CFO_PACKAGES: DiligencePackage[] = [
  {
    id: 'revenue-health',
    title: 'Revenue health',
    description:
      'Top-line trend, service-line mix, and gross-margin trajectory.',
    accent: 'wb',
    questions: [
      'Revenue by service line — 179D, 45L, ITC, energy modeling, energy audits — for the last 12 months.',
      'How much revenue did we generate from 179D work last year, and how does that compare to the prior year?',
      'What is the gross margin trend by quarter for 2025?',
      'Which months have been the strongest historically, and what is driving the seasonality?',
    ],
  },
  {
    id: 'concentration-risk',
    title: 'Concentration risk',
    description:
      'Customer + service-line concentration, top accounts, churn signals.',
    accent: 'amber',
    questions: [
      'Do we have concentration risk with any major client or service line?',
      'Top 10 clients by revenue in 2025 — what share of total revenue do they represent?',
      'Which clients have stopped engaging with us in the last 12 months?',
      'What share of revenue comes from clients that have been with us for less than 12 months vs more than 3 years?',
    ],
  },
  {
    id: 'growth-pipeline',
    title: 'Growth & pipeline',
    description:
      'Fastest-growing services, new clients, average deal trajectory.',
    accent: 'emerald',
    questions: [
      'Which services have grown the fastest in the last 12 months?',
      'How many new clients did we sign in 2025 vs 2024, and what is their combined revenue?',
      'What is the average revenue per engagement, and how has it trended over the last 3 years?',
      'Where is the most repeat business coming from, and what is the typical reorder cycle?',
    ],
  },
  {
    id: 'data-quality',
    title: 'Data quality & gaps',
    description:
      'What we can answer cleanly, what requires inference, and what is missing.',
    accent: 'violet',
    questions: [
      "What parts of our QuickBooks data aren't tracked cleanly enough to answer executive-level questions?",
      'Which questions about Walker Blue would require data we do NOT currently capture?',
      'What is the inferred customer-type breakdown, and how reliable is that inference?',
      'Are there inconsistencies in how service lines are tagged across years that would affect trend analysis?',
    ],
  },
];

const CFO_SUGGESTIONS: SingleSuggestion[] = [
  {
    category: 'Revenue',
    text: 'Revenue by service line — 179D, 45L, ITC, energy modeling, energy audits',
  },
  {
    category: 'Growth',
    text: 'Which services have grown the fastest in the last 12 months?',
  },
  {
    category: 'Risk',
    text: 'Do we have concentration risk with any major client or service line?',
  },
  {
    category: 'Data quality',
    text: "What parts of our QuickBooks data aren't tracked cleanly enough to answer executive-level questions?",
  },
];

const COO_PACKAGES: DiligencePackage[] = [
  {
    id: 'inspection-throughput',
    title: 'Inspection throughput',
    description:
      'Top inspectors, completed-inspection volume, project velocity.',
    accent: 'teal',
    questions: [
      'Top 5 inspectors by completed inspection count over the last 12 months.',
      'How many inspections did our team complete last quarter, and how does that compare to the prior quarter?',
      'What is the average days-to-completion per inspection, and which inspectors trend fastest?',
      'Are any inspectors carrying disproportionate load right now? Show their open / in-progress queue.',
    ],
  },
  {
    id: 'geographic-coverage',
    title: 'Geographic coverage',
    description:
      'Where we work, where we are growing, where we have white space.',
    accent: 'sky',
    questions: [
      'Inspections by state for the last 12 months — top 10 states.',
      'Which states have we never worked in? Where are the geographic gaps?',
      'Average inspection turnaround by state — where are we slowest, and is travel a factor?',
      'Where are our buildings concentrated, and how does that map to where the inspectors live?',
    ],
  },
  {
    id: 'project-pipeline',
    title: 'Project pipeline',
    description:
      '179D + ITC workflow status, RFI submissions, deal stage distribution.',
    accent: 'emerald',
    questions: [
      'How many active opportunities are in Insightly right now (both 179D and ITC)? Break out by service line and stage.',
      'How many RFI submissions are open, and what is the average response time?',
      'Which projects have the longest gap between submission and first inspection?',
      'Top 10 referral firms by project volume in the last 12 months — split between 179D and ITC.',
    ],
  },
  {
    id: 'resource-utilization',
    title: 'Resource utilization',
    description:
      'Inspector availability, per-diem patterns, capacity gaps.',
    accent: 'amber',
    questions: [
      'Which inspectors have the most unavailability flagged in the next 60 days?',
      'Average per-diem days per inspector last quarter — where are the outliers?',
      'Are we capacity-constrained anywhere? Inspections scheduled vs inspector hours available.',
      'Re-inspection rate by inspector — where is QA flagging the most rework?',
    ],
  },
];

const COO_SUGGESTIONS: SingleSuggestion[] = [
  {
    category: 'Throughput',
    text: 'How many inspections did our team complete last quarter?',
  },
  {
    category: 'Coverage',
    text: 'Top 5 inspectors by completed inspection count over the last 12 months.',
  },
  {
    category: 'Pipeline',
    text: 'How many active opportunities are in Insightly across 179D and ITC?',
  },
  {
    category: 'Buildings',
    text: 'How many buildings are in our catalog total?',
  },
];

/**
 * Per-persona content for the EmptyState screen. New personas get their
 * entry here; the EmptyState component looks up by personaId and falls
 * back to CFO if a persona isn't yet defined (CMO/CEO inactive today).
 */
export interface PersonaEmptyStateContent {
  /** Tagline shown under the eyebrow. */
  headline: string;
  /** Description sentence explaining the data sources. */
  description: string;
  packages: DiligencePackage[];
  suggestions: SingleSuggestion[];
}

const CEO_PACKAGES: DiligencePackage[] = [
  {
    id: 'board-health',
    title: 'Board-level health check',
    description:
      'Quarterly snapshot — revenue, throughput, pipeline, and the single biggest risk you should know about.',
    accent: 'wb',
    questions: [
      'Give me a board-ready snapshot of how the business is doing this quarter — revenue, top customers, inspection throughput, pipeline value, and the single biggest risk.',
      'How are we tracking against last year on revenue and on inspections completed?',
      "What's the one number that has changed the most quarter-over-quarter, and why?",
      'What are the top 5 KPIs I should review before tomorrow\'s board meeting?',
    ],
  },
  {
    id: 'cross-functional-risk',
    title: 'Cross-functional risk',
    description:
      'Where revenue, operations, and pipeline risk overlap — concentration, capacity, and cash.',
    accent: 'amber',
    questions: [
      'Where are the biggest concentration risks in our customer base, and what share of revenue do the top 5 represent?',
      'Are we capacity-constrained anywhere — inspections demanded vs inspector hours available, by region?',
      'What share of pipeline value is in the top 3 opportunities, and what happens if any of them slip?',
      'Open AR over 60 days plus inspections delayed past 30 days — anywhere those cluster on the same customer?',
    ],
  },
  {
    id: 'growth-pipeline',
    title: 'Growth & pipeline',
    description:
      'Closed-won, qualified pipeline, win rate, and where new business is coming from.',
    accent: 'emerald',
    questions: [
      'How much closed-won did we book in the last 12 months across 179D and ITC, and what is the qualified pipeline value today?',
      'What is our 179D vs ITC win rate, and how has it trended over the last 4 quarters?',
      'Top 10 referral firms by closed-won value over the last 12 months — and which of them are accelerating vs slowing?',
      'What new customer segments are showing up in the pipeline that we did not see a year ago?',
    ],
  },
  {
    id: 'recall-prior-context',
    title: 'Synthesize across executives',
    description:
      'Pull together what the CFO and COO have already answered into one coherent picture.',
    accent: 'violet',
    questions: [
      'Summarize what we have learned about Walker Blue across all my prior conversations with the CFO and COO.',
      'Where do the financial numbers (QuickBooks) and the pipeline numbers (Insightly) disagree, and what is the likely reason?',
      'What corrections have I given in earlier conversations that should be treated as ground truth going forward?',
      'Which open questions from prior sessions have we never gone back to resolve?',
    ],
  },
];

const CEO_SUGGESTIONS: SingleSuggestion[] = [
  {
    category: 'Board snapshot',
    text: 'Give me a board-ready snapshot of how the business is doing this quarter.',
  },
  {
    category: 'Risk',
    text: 'Where are the biggest concentration risks in our customer base?',
  },
  {
    category: 'Pipeline',
    text: 'How much closed-won did we book in the last 12 months across 179D and ITC?',
  },
  {
    category: 'Synthesis',
    text: 'Summarize what we have learned about Walker Blue across all my prior conversations.',
  },
];

export const EMPTY_STATE_BY_PERSONA: Record<string, PersonaEmptyStateContent> = {
  cfo: {
    headline: "Ask anything about Walker Blue's financials.",
    description:
      "Connected to QuickBooks, Amex expense history, and the internal knowledge vault. Every answer cites its sources; every chart is exportable.",
    packages: CFO_PACKAGES,
    suggestions: CFO_SUGGESTIONS,
  },
  coo: {
    headline: "Ask anything about Walker Blue's operations.",
    description:
      "Connected to reactIMS inspections, the building catalog, ITC Quality Control & RFI submissions, the d2o workflow with Insightly CRM (179D + ITC opportunities, pipelines, projects), and the internal knowledge vault.",
    packages: COO_PACKAGES,
    suggestions: COO_SUGGESTIONS,
  },
  ceo: {
    headline: 'Ask anything across the business.',
    description:
      "Connected to every system the other executives use — QuickBooks, Amex, reactIMS inspections, the building catalog, ITC Quality Control + RFI, the d2o workflow with Insightly CRM, and the internal knowledge vault. Plus visibility into ALL prior conversations across executives, so I can synthesize what the CFO and COO have already answered.",
    packages: CEO_PACKAGES,
    suggestions: CEO_SUGGESTIONS,
  },
};

/**
 * Default fallback so a persona without explicit content (CMO/CEO before
 * they're activated) doesn't render an empty page.
 */
export const DEFAULT_EMPTY_STATE: PersonaEmptyStateContent = EMPTY_STATE_BY_PERSONA['cfo']!;

/**
 * Backwards-compat export for any callers that used the old flat list.
 * Same shape as before, points at the CFO packages.
 */
export const DILIGENCE_PACKAGES: DiligencePackage[] = CFO_PACKAGES;

export function findPackage(id: string | undefined): DiligencePackage | undefined {
  if (!id) return undefined;
  for (const personaContent of Object.values(EMPTY_STATE_BY_PERSONA)) {
    const found = personaContent.packages.find((p) => p.id === id);
    if (found) return found;
  }
  return undefined;
}
